// Service for fetching and parsing a single property from Altia Marketplace.
//
// Altia (marketplace.altia.com.cy) is a Laravel + Vue SPA backed by a REST API
// at api.marketplace.altia.com.cy. Individual listings are served, unauthenticated,
// from `GET /v1/listings/{id}` — no CSRF handshake is required for reads.
//
// The API restricts CORS to the marketplace origin, so the browser cannot call
// it directly. We route the request through the shared CORS proxy (same path
// BuySell uses), reading the JSON body.

import { createNormalizedPropertyData } from './propertyDataSchema';
import { fetchViaProxies } from './proxyFetch';
import { sanitizeText } from './buysellService';

const ALTIA_API = 'https://api.marketplace.altia.com.cy';

/**
 * Extract the numeric listing ID from an Altia URL.
 * Supported: /listings/2788, /en/listings/2788, /el/listings/2788, etc.
 * @param {string} url - Altia property URL
 * @returns {string|null}
 */
export const extractAltiaId = (url) => {
  if (!url) return null;
  const match = url.match(/\/listings\/(\d+)/i);
  return match ? match[1] : null;
};

/**
 * Pick a value from Altia's localized arrays.
 * Entries look like [{ locale: 'en', value: '...' }] or sometimes [{ value: '...' }].
 * Prefers the English entry, falling back to the first available.
 * @param {Array} arr
 * @param {string} locale
 * @returns {string}
 */
const altiaLocalized = (arr, locale = 'en') => {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  const hit = arr.find((x) => x && x.locale === locale) || arr[0];
  return (hit && hit.value) || '';
};

/** Decode the handful of HTML entities Altia descriptions contain. */
const decodeEntities = (text) =>
  (text || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');

/** Title-case a location slug or token ("larnaca-kiti" -> "Larnaca Kiti"). */
const titleCase = (str) =>
  (str || '')
    .replace(/[-_,]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

/**
 * Build a human-readable location from Altia's address block.
 * Prefers the LRO location ("Larnaca,KITI"), then the most specific
 * location slug, then a generic fallback.
 */
const buildAltiaLocation = (propertyInfo) => {
  const address = propertyInfo?.address || {};
  const lroLocation = propertyInfo?.lro?.lroIds?.[0]?.location;
  if (lroLocation) return titleCase(lroLocation);

  const locations = Array.isArray(address.locations) ? address.locations : [];
  if (locations.length > 0) {
    // The last slug is typically the most specific (e.g. "larnaca-kiti").
    return titleCase(locations[locations.length - 1]);
  }
  return 'Cyprus';
};

/**
 * Detect sale/rent for an Altia property.
 * @param {string} url
 * @param {Object} [normalizedData]
 * @returns {{forSale: boolean, forRent: boolean}}
 */
export const detectAltiaPropertyType = (url, normalizedData = null) => {
  if (normalizedData && (normalizedData.forSale || normalizedData.forRent)) {
    return { forSale: normalizedData.forSale, forRent: normalizedData.forRent };
  }

  const urlLower = (url || '').toLowerCase();
  const forRent = urlLower.includes('/rental') || urlLower.includes('/rent');
  const forSale = urlLower.includes('/sale') || (!forRent);
  return { forSale, forRent };
};

/**
 * Convert an Altia `GET /v1/listings/{id}` response body into normalized data.
 * @param {Object} body - Parsed API response
 * @param {string} propertyUrl
 * @returns {Object} - Normalized property data
 */
const altiaListingToNormalized = (body, propertyUrl) => {
  const data = body?.data || {};
  const meta = body?.meta || {};
  const info = data.listingInfo || {};
  const propertyInfo = data.propertyInfo || {};
  const aggregations = data.aggregations || {};

  const priceOnRequest = !!meta.priceUponRequest;

  const title = sanitizeText(altiaLocalized(info.title), 200);
  const description = sanitizeText(
    decodeEntities(altiaLocalized(info.description)),
    3000
  );

  const price =
    typeof info.price?.amount === 'number' ? info.price.amount : 0;

  const area = propertyInfo.size != null ? String(propertyInfo.size) : '';
  const bedrooms =
    aggregations.bedrooms != null ? String(aggregations.bedrooms) : '';
  const bathrooms =
    aggregations.bathrooms != null ? String(aggregations.bathrooms) : '';

  const photos = data.mediaInfo?.images?.photos;
  let imageURL = '';
  if (Array.isArray(photos) && photos.length > 0) {
    const sizes = photos[0].sizes || {};
    imageURL = sizes.lg || sizes.md || sizes.xl || sizes.sm || '';
  }

  const coords = propertyInfo.address?.coordinates || {};
  const latitude = typeof coords.lat === 'number' ? coords.lat : null;
  const longitude = typeof coords.lng === 'number' ? coords.lng : null;

  const location = buildAltiaLocation(propertyInfo);

  const buildYear = propertyInfo.building?.buildYear;
  const constructionYear = buildYear ? String(buildYear) : 'N/A';

  const parking =
    aggregations.parkingSpaces != null && aggregations.parkingSpaces > 0
      ? String(aggregations.parkingSpaces)
      : 'N/A';

  const publishedDate = data.timestamps?.createdAt
    ? new Date(data.timestamps.createdAt).toISOString().split('T')[0]
    : '';
  const updatedDate = data.timestamps?.updatedAt
    ? new Date(data.timestamps.updatedAt).toISOString().split('T')[0]
    : '';

  const commercialization = (info.commercializationType || 'sale').toLowerCase();
  const forSale = commercialization === 'sale';
  const forRent = commercialization === 'rental' || commercialization === 'rent';

  const agent = info.agent || {};
  const landlordName = sanitizeText(
    [agent.firstName, agent.lastName].filter(Boolean).join(' '),
    200
  );

  return createNormalizedPropertyData({
    title,
    description,
    price,
    priceOnRequest,
    publishedDate,
    updatedDate,
    area,
    bedrooms,
    bathrooms,
    imageURL,
    latitude,
    longitude,
    location,
    parking,
    constructionYear,
    landlordName,
    forSale: forSale || (!forRent),
    forRent,
  });
};

/**
 * Fetch a single Altia listing via their REST API (through the CORS proxy).
 * @param {string} propertyUrl - Altia property URL
 *   (e.g. https://marketplace.altia.com.cy/en/listings/2788)
 * @returns {Promise<Object>} - Normalized property data
 */
export const fetchAltiaData = async (propertyUrl) => {
  if (!propertyUrl || !propertyUrl.trim()) {
    throw new Error('Property URL is required');
  }

  const propertyId = extractAltiaId(propertyUrl);
  if (!propertyId) {
    throw new Error('Invalid Altia URL: Cannot extract listing ID');
  }

  const apiUrl = `${ALTIA_API}/v1/listings/${propertyId}`;

  const body = await fetchViaProxies(apiUrl, {
    responseType: 'json',
    timeout: 20000,
    label: 'Altia',
    validate: (json) => !!(json && json.data && json.data.id),
  });

  if (!body?.data?.id) {
    throw new Error(
      `Listing #${propertyId} not found on Altia. It may have been removed.`
    );
  }

  return altiaListingToNormalized(body, propertyUrl);
};
